// src/admin/Settings.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Save, Upload, Image as ImageIcon, Leaf, X, Globe, Phone, Mail,
  CreditCard, Truck, Building2, FileText, Type
} from 'lucide-react';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube } from 'react-icons/fa';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState({
    siteName: 'Organic Land',
    logo: '',
    phone: '+92 300 1234567',
    email: 'hello@organicland.com',
    address: 'Lahore, Pakistan',
    storeLocationUrl: '',
    freeShippingText: 'Free Shipping on Orders Rs.2000+',
    codEnabled: true,
    bankTransferEnabled: true,
    bankName: '',
    accountTitle: '',
    accountNumber: '',
    iban: '',
    qrCode: '',
    bankInstructions: 'Bank/Easypaisa/JazzCash se payment karein, phir Transaction ID aur Screenshot upload karein.',
    footerAbout: 'Premium quality fresh organic products sourced directly from certified organic farms.',
    footerFacebook: '',
    footerInstagram: '',
    footerTwitter: '',
    footerYoutube: '',
    footerCopyright: '© 2025 Organic Land. All rights reserved. | Designed with care in Pakistan',
    // Home Page Section Titles
    categoryTitle: 'Shop by Category',
    categorySubtitle: 'Browse our wide range of organic products',
    featuredTitle: 'Featured Products',
    featuredSubtitle: 'Hand-picked organic products for you',
    allProductsTitle: 'All Products',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(prev => ({ ...prev, ...docSnap.data() }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToImageKit(file, 'logo');
      setSettings({ ...settings, logo: url });
      setMessage({ type: 'success', text: 'Logo uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Logo upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingQR(true);
    try {
      const url = await uploadToImageKit(file, 'qrcode');
      setSettings({ ...settings, qrCode: url });
      setMessage({ type: 'success', text: 'QR Code uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'QR upload failed.' });
    } finally {
      setUploadingQR(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await setDoc(doc(db, 'settings', 'site'), settings, { merge: true });
      setMessage({ type: 'success', text: 'Settings saved!' });

      // Notify all components to refresh
      window.dispatchEvent(new Event('settingsUpdated'));

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">Website Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Logo, contact info, payment, aur footer yahan se manage karein.</p>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto" style={{ borderColor: '#e5e7eb' }}>
        {[
          { key: 'general', label: 'General', icon: Globe },
          { key: 'sections', label: 'Home Sections', icon: Type },
          { key: 'payment', label: 'Payment', icon: CreditCard },
          { key: 'footer', label: 'Footer', icon: FileText },
        ].map(t => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm whitespace-nowrap transition-all ${active ? 'text-green-700 border-b-2 border-green-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Leaf size={20} className="text-green-700" /> Logo & Site Name
              </h2>

              <div className="mb-5 flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                {settings.logo ? (
                  <img src={settings.logo} alt="Logo" className="h-12 w-auto object-contain" />
                ) : (
                  <div className="flex items-center gap-2">
                    <Leaf size={32} className="text-green-700" />
                    <span className="text-xl font-bold text-green-900">{settings.siteName}</span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Logo</label>
                {settings.logo ? (
                  <div className="flex gap-2">
                    <label className="flex-1 border border-gray-300 rounded-lg py-3 px-4 text-center cursor-pointer hover:bg-gray-50 text-sm font-semibold text-gray-700">
                      <Upload size={16} className="inline mr-2" /> Change Logo
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                    </label>
                    <button type="button" onClick={() => setSettings({ ...settings, logo: '' })}
                      className="px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold flex items-center gap-1">
                      <X size={16} /> Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                        <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="text-gray-400 mb-2" size={32} />
                        <p className="text-sm font-semibold text-gray-600">Click to upload logo</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Site Name</label>
                <input type="text" value={settings.siteName}
                  onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Globe size={20} className="text-green-700" /> Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input type="tel" value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input type="email" value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Store Address</label>
                  <input type="text" value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Google Maps URL</label>
                  <input type="url" value={settings.storeLocationUrl}
                    onChange={(e) => setSettings({ ...settings, storeLocationUrl: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="https://maps.google.com/..." />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Free Shipping Text</label>
                  <input type="text" value={settings.freeShippingText}
                    onChange={(e) => setSettings({ ...settings, freeShippingText: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* HOME SECTIONS TAB */}
        {activeTab === 'sections' && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Type size={20} className="text-green-700" /> Category Section
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Section Title</label>
                  <input type="text" value={settings.categoryTitle || ''}
                    onChange={(e) => setSettings({ ...settings, categoryTitle: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Shop by Category" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Section Subtitle</label>
                  <input type="text" value={settings.categorySubtitle || ''}
                    onChange={(e) => setSettings({ ...settings, categorySubtitle: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Browse our wide range of organic products" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Type size={20} className="text-green-700" /> Featured Products Section
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Section Title</label>
                  <input type="text" value={settings.featuredTitle || ''}
                    onChange={(e) => setSettings({ ...settings, featuredTitle: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Featured Products" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Section Subtitle</label>
                  <input type="text" value={settings.featuredSubtitle || ''}
                    onChange={(e) => setSettings({ ...settings, featuredSubtitle: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Hand-picked organic products for you" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Type size={20} className="text-green-700" /> All Products Section
              </h2>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Section Title</label>
                <input type="text" value={settings.allProductsTitle || ''}
                  onChange={(e) => setSettings({ ...settings, allProductsTitle: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="All Products" />
              </div>
            </div>
          </>
        )}

        {/* PAYMENT TAB */}
        {activeTab === 'payment' && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Truck size={20} className="text-green-700" /> Cash on Delivery (COD)
              </h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={settings.codEnabled}
                  onChange={(e) => setSettings({ ...settings, codEnabled: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Enable COD on Checkout</span>
              </label>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <Building2 size={20} className="text-green-700" /> Bank Transfer
              </h2>

              <label className="flex items-center gap-3 cursor-pointer mb-6">
                <input type="checkbox" checked={settings.bankTransferEnabled}
                  onChange={(e) => setSettings({ ...settings, bankTransferEnabled: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Enable Bank Transfer on Checkout</span>
              </label>

              {settings.bankTransferEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                    <input type="text" value={settings.bankName}
                      onChange={(e) => setSettings({ ...settings, bankName: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="e.g., Meezan Bank" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Title</label>
                    <input type="text" value={settings.accountTitle}
                      onChange={(e) => setSettings({ ...settings, accountTitle: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="e.g., Organic Land" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                    <input type="text" value={settings.accountNumber}
                      onChange={(e) => setSettings({ ...settings, accountNumber: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="e.g., 0300-1234567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">IBAN (Optional)</label>
                    <input type="text" value={settings.iban}
                      onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="PK00XXXX0000000000000000" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Instructions</label>
                    <textarea value={settings.bankInstructions}
                      onChange={(e) => setSettings({ ...settings, bankInstructions: e.target.value })}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">QR Code (Optional)</label>
                    {settings.qrCode ? (
                      <div className="flex items-center gap-3">
                        <img src={settings.qrCode} alt="QR" className="w-32 h-32 object-contain rounded-lg border border-gray-200" />
                        <div className="flex flex-col gap-2">
                          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm font-semibold text-gray-700">
                            <Upload size={14} className="inline mr-1" /> Change
                            <input type="file" accept="image/*" onChange={handleQRUpload} className="hidden" disabled={uploadingQR} />
                          </label>
                          <button type="button" onClick={() => setSettings({ ...settings, qrCode: '' })}
                            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold">
                            <X size={14} className="inline mr-1" /> Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                        {uploadingQR ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                        ) : (
                          <>
                            <ImageIcon className="text-gray-400 mb-2" size={28} />
                            <p className="text-sm font-semibold text-gray-600">Click to upload QR Code</p>
                          </>
                        )}
                        <input type="file" accept="image/*" onChange={handleQRUpload} className="hidden" disabled={uploadingQR} />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* FOOTER TAB */}
        {activeTab === 'footer' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
              <FileText size={20} className="text-green-700" /> Footer Settings
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">About Text</label>
                <textarea value={settings.footerAbout}
                  onChange={(e) => setSettings({ ...settings, footerAbout: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <FaFacebook size={14} /> Facebook URL
                  </label>
                  <input type="url" value={settings.footerFacebook}
                    onChange={(e) => setSettings({ ...settings, footerFacebook: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="https://facebook.com/..." />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <FaInstagram size={14} /> Instagram URL
                  </label>
                  <input type="url" value={settings.footerInstagram}
                    onChange={(e) => setSettings({ ...settings, footerInstagram: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <FaTwitter size={14} /> Twitter URL
                  </label>
                  <input type="url" value={settings.footerTwitter}
                    onChange={(e) => setSettings({ ...settings, footerTwitter: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="https://twitter.com/..." />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <FaYoutube size={14} /> YouTube URL
                  </label>
                  <input type="url" value={settings.footerYoutube}
                    onChange={(e) => setSettings({ ...settings, footerYoutube: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="https://youtube.com/..." />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Copyright Text</label>
                <input type="text" value={settings.footerCopyright}
                  onChange={(e) => setSettings({ ...settings, footerCopyright: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`bg-green-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 shadow-xl ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default Settings;